/**
 * Foundation path intro copy + variant selection.
 *
 * Owns the exact text of every programmatic intro used during the Foundations
 * onboarding flow. ModeOrchestrator._serveOrchestratorQuestionnaire() calls
 * resolveFoundationIntro() and either uses the returned text (fast path) or
 * falls through to the Haiku discovery_intro path when null is returned.
 *
 * See project-docs/temporary/IMMEDIATE (archived) for design context.
 */

const LIFE_FACTS = {
  fromOrientation: () =>
    `Great — let's keep going. We'll start by getting some basics about you and your relationship, and then we'll dig into what's actually going on. This first part is quick.\n\nClick on the questionnaire below to begin.`,
  welcomeBack: ({ firstName }) =>
    `Welcome back, ${firstName}.\n\nDuring orientation, we explored your affection patterns — now let's build on that. We'll start with some basics about you and your relationship to give me context, and then we'll dig into what's actually going on.\n\nClick on the questionnaire below to begin.`
};

const RELATIONSHIP_SCREENING = {
  welcomeBack: ({ firstName }) =>
    `Welcome back, ${firstName}.\n\nLet's pick up where we left off. Last time, you completed the **About You** questionnaire which gave me some helpful context.\n\nNow, I'd like to get a quick read on how different parts of your relationship are going, things like communication, trust, intimacy, and conflict.\n\nClick on the questionnaire below when you're ready.`,
  default: () =>
    `Thanks — that's very helpful. Now I'd like to get a quick read on how different parts of your relationship are going — things like communication, trust, intimacy, and conflict.\n\nClick on the questionnaire below when you're ready.`
};

const SECTION_INTROS = {
  '01.01.01': {
    default: () =>
      `Let's start with the big picture. This next questionnaire is quite brief, but will tell me about your overall sense of commitment and connection in your relationship.\n\nClick below when you're ready.`,
    resumed: ({ firstName }) =>
      `Welcome back, ${firstName}.\n\nLet's start with the big picture. This next questionnaire is quite brief, but will tell me about your overall sense of commitment and connection in your relationship.\n\nClick below when you're ready.`
  },
  '07.01.01': {
    default: () =>
      `Next, I'd like to understand your attachment style: how you tend to experience closeness, independence, and security in relationships. This is one of the most useful lenses we have for making sense of what comes up between partners.\n\nClick below when you're ready.`,
    resumed: ({ firstName }) =>
      `Welcome back, ${firstName}.\n\nLet's pick up where we left off. Next is a quick look at your attachment style — how you tend to experience closeness and security in relationships. This is one of the most useful lenses we have for making sense of what comes up between partners.\n\nClick below when you're ready.`
  },
  '03.01.01': {
    default: () =>
      `Now let's look at the emotional side of your relationship — how connected, understood, and attuned you feel with your partner day-to-day. This questionnaire is brief but will generate some important data.\n\nClick below when you're ready.`,
    resumed: ({ firstName }) =>
      `Welcome back, ${firstName}.\n\nLet's pick up where we left off. Next, we'll look at the emotional side of your relationship — how connected and attuned you feel with your partner. This questionnaire is brief but will generate some important data.\n\nClick below when you're ready.`
  },
  '03.02.01': {
    default: () =>
      `Now let's look at how you give and receive affection — your love languages. This questionnaire is a bit longer than the others, but it tends to surface patterns that are easy to feel but hard to put into words.\n\nClick below when you're ready.`,
    resumed: ({ firstName }) =>
      `Welcome back, ${firstName}.\n\nLet's pick up where we left off. Next, we'll look at how you give and receive affection — your love languages. It's a bit longer than the others, but it tends to surface patterns that are easy to feel but hard to put into words.\n\nClick below when you're ready.`
  },
  '16.01.01': {
    default: () =>
      `Lastly, let's look at your unique personality. This next questionnaire focuses on you as an individual — your patterns, tendencies, and inner world — since who you are shapes how you show up in the relationship.\n\nClick below when you're ready.`,
    resumed: ({ firstName }) =>
      `Welcome back, ${firstName}.\n\nLet's pick up where we left off. This next questionnaire looks at your personality — your tendencies, how you think and react — since who you are shapes how you show up in the relationship.\n\nClick below when you're ready.`
  }
};

/**
 * Resolve a programmatic Foundation intro, or null to fall through to Haiku.
 *
 * @param {Object} params
 * @param {Object} params.stepData      Orchestrator step payload
 * @param {Object} params.frontendState { firstName, hadWelcomeSignal, pathIntroduced, fromOrientation }
 * @returns {{ text: string, clearFromOrientation: boolean } | null}
 */
export function resolveFoundationIntro({ stepData, frontendState }) {
  if (!stepData) return null;

  // Universal gates: only fires for fresh Foundation intros (no re-serves).
  const entryPathId = stepData.pathId;
  if (entryPathId && entryPathId !== 'foundation') return null;
  if (stepData.reServe) return null;
  if (stepData.isL2ReServe) return null;

  const { firstName, hadWelcomeSignal, pathIntroduced, fromOrientation } = frontendState;
  const questionnaireId = stepData.questionnaire?.id;

  // life-facts: gated to first-ever entry (matches legacy hardcoded behavior).
  // `fromOrientation` overrides the `pathIntroduced` proxy because orientation
  // pre-completes the love-languages questionnaire (also part of foundation),
  // making `percentComplete > 0` on first foundation entry from orientation.
  if (questionnaireId === 'life-facts') {
    const isFreshEntry = !pathIntroduced || fromOrientation;
    if (!(hadWelcomeSignal && stepData.isPrerequisite && isFreshEntry && stepData.chain?.position === 1)) {
      return null;
    }
    const variant = fromOrientation ? LIFE_FACTS.fromOrientation : LIFE_FACTS.welcomeBack;
    return { text: variant({ firstName }), clearFromOrientation: true };
  }

  if (questionnaireId === 'relationship-screening') {
    const variant = hadWelcomeSignal ? RELATIONSHIP_SCREENING.welcomeBack : RELATIONSHIP_SCREENING.default;
    return { text: variant({ firstName }), clearFromOrientation: false };
  }

  // Section-code questionnaires (01.01.01 CSI-4, 07.01.01 ECR-12, 03.01.01, 03.02.01 affectionpatterns, 16.01.01).
  // Match on sectionCode — stepData.questionnaire.id is the questionnaire's own id, not the section.
  const section = SECTION_INTROS[stepData.sectionCode];
  if (section) {
    const variant = hadWelcomeSignal ? section.resumed : section.default;
    return { text: variant({ firstName }), clearFromOrientation: false };
  }

  return null;
}
